import React, { useRef } from 'react';
import { CheckIcon, ArrowRightIcon, ShareIcon, DownloadIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { StatusBar } from './StatusBar';
import type { TransferSummary } from './TransferFormScreen';
export function PaymentReceiptScreen({
  summary,
  onClose,
  onTransferAgain
}: {
  summary: TransferSummary;
  onClose: () => void;
  onTransferAgain: () => void;
}) {
  const ref = useReference();
  const now = useNow();
  const amount = formatAmount(summary.amount);
  const fee = summary.mode === 'another' ? 25 : 0;
  const totalDebited = parseAmount(summary.amount) + fee;
  return <div className="relative flex flex-col w-full h-full overflow-hidden" style={{
    backgroundColor: '#F4F7FA'
  }} data-id="element-508">
      <StatusBar data-id="element-509" />

      {/* Close */}
      <div className="flex items-center justify-end px-5 pt-2 pb-3" data-id="element-510">
        <button type="button" aria-label="Close" onClick={onClose} className="flex items-center justify-center rounded-full bg-white transition-colors active:bg-neutral-100" style={{
        width: 40,
        height: 40,
        border: '1px solid #E5EAF0'
      }} data-id="element-511">
          <XIcon size={18} style={{
          color: '#1A1A1A'
        }} data-id="element-512" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-3" data-id="element-513">
        {/* Success icon + headline */}
        <div className="flex flex-col items-center text-center" data-id="element-514">
          <span className="relative flex items-center justify-center rounded-full" style={{
          width: 84,
          height: 84,
          background: 'radial-gradient(circle, rgba(16,185,129,0.18) 0%, transparent 70%)'
        }} data-id="element-515">
            <span className="flex items-center justify-center rounded-full" style={{
            width: 60,
            height: 60,
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            boxShadow: '0 14px 28px -10px rgba(16,185,129,0.55)'
          }} data-id="element-516">
              <CheckIcon size={30} color="#FFFFFF" strokeWidth={3.5} data-id="element-517" />
            </span>
          </span>
          <div className="mt-4 tracking-tight" style={{
          fontSize: 22,
          fontWeight: 800,
          color: '#0F172A',
          letterSpacing: '-0.01em'
        }} data-id="element-518">
            Transfer Successful
          </div>
          <div className="mt-1.5 tracking-tight" style={{
          fontSize: 32,
          fontWeight: 800,
          color: '#0F172A',
          letterSpacing: '-0.02em'
        }} data-id="element-519">
            LKR {amount}
          </div>
          <div className="mt-1.5" style={{
          fontSize: 12,
          color: '#64748B',
          fontWeight: 500
        }} data-id="element-520">
            {now} · Ref{' '}
            <span style={{
            color: '#0F172A',
            fontWeight: 700
          }} data-id="element-521">
              {ref}
            </span>
          </div>
        </div>

        {/* Receipt card */}
        <div className="relative rounded-3xl bg-white mt-6 overflow-hidden" style={{
        border: '1px solid #E5EAF0',
        boxShadow: '0 18px 40px -22px rgba(15,23,42,0.18)'
      }} data-id="element-522">
          {/* From → To */}
          <div className="p-5" data-id="element-523">
            <Party label="From" name={(summary.source || '—').split(' · ')[0]} sub={(summary.source || '').split(' · ')[1]} tone="neutral" data-id="element-524" />
            <div className="flex items-center justify-center my-2.5" data-id="element-525">
              <span className="flex items-center justify-center rounded-full" style={{
              width: 28,
              height: 28,
              backgroundColor: '#E6F7FB',
              color: '#009AC7'
            }} data-id="element-526">
                <ArrowRightIcon size={14} strokeWidth={3} style={{
                transform: 'rotate(90deg)'
              }} data-id="element-527" />
              </span>
            </div>
            <Party label="To" name={(summary.recipient || 'Recipient').split(' · ')[0]} sub={(summary.recipient || '').split(' · ')[1] || summary.accountNo} tone="brand" data-id="element-528" />
          </div>

          {/* Dashed divider — modern receipt cue */}
          <div className="relative" data-id="element-529">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full" style={{
            width: 16,
            height: 16,
            backgroundColor: '#F4F7FA',
            marginLeft: -8
          }} aria-hidden="true" data-id="element-530" />
            <div className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full" style={{
            width: 16,
            height: 16,
            backgroundColor: '#F4F7FA',
            marginRight: -8
          }} aria-hidden="true" data-id="element-531" />
            <div className="mx-5 border-t border-dashed" style={{
            borderColor: '#CBD5E1'
          }} data-id="element-532" />
          </div>

          {/* Details */}
          <div className="p-5" data-id="element-533">
            <DetailRow label="Amount" value={`LKR ${amount}`} data-id="element-534" />
            <DetailRow label="Fee" value={fee === 0 ? 'Free' : `LKR ${fee.toFixed(2)}`} tone={fee === 0 ? 'free' : undefined} data-id="element-535" />
            <DetailRow label="Total debited" value={`LKR ${formatAmount(totalDebited.toString())}`} emphasis data-id="element-536" />
            {summary.reference && <DetailRow label="Reference" value={summary.reference} last data-id="element-537" />}
          </div>
        </div>

        {/* Action row */}
        <div className="mt-5 grid grid-cols-2 gap-3" data-id="element-538">
          <button type="button" className="flex items-center justify-center gap-2 rounded-2xl bg-white transition-all active:scale-[0.98]" style={{
          minHeight: 52,
          border: '1px solid #E5EAF0',
          color: '#0F172A',
          fontSize: 13.5,
          fontWeight: 700
        }} data-id="element-539">
            <ShareIcon size={17} data-id="element-540" />
            Share
          </button>
          <button type="button" className="flex items-center justify-center gap-2 rounded-2xl bg-white transition-all active:scale-[0.98]" style={{
          minHeight: 52,
          border: '1px solid #E5EAF0',
          color: '#0F172A',
          fontSize: 13.5,
          fontWeight: 700
        }} data-id="element-541">
            <DownloadIcon size={17} data-id="element-542" />
            Download
          </button>
        </div>

        <div className="mt-5 flex items-center justify-center gap-1.5" data-id="element-543">
          <ShieldCheckIcon size={13} style={{
          color: '#10B981'
        }} data-id="element-544" />
          <span style={{
          fontSize: 11.5,
          color: '#64748B',
          fontWeight: 500
        }} data-id="element-545">
            Receipt sent to your registered email
          </span>
        </div>
      </div>

      {/* Sticky footer CTAs */}
      <div className="px-5 pt-3 pb-3" style={{
      backgroundColor: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderTop: '1px solid #E5EAF0'
    }} data-id="element-546">
        <button type="button" onClick={onClose} className="w-full rounded-2xl text-white font-semibold transition-all active:scale-[0.99]" style={{
        backgroundColor: '#009AC7',
        minHeight: 56,
        fontSize: 16,
        boxShadow: '0 12px 28px -10px rgba(0,154,199,0.55)',
        letterSpacing: '0.01em'
      }} data-id="element-547">
          Done
        </button>
        <button type="button" onClick={onTransferAgain} className="w-full mt-2" style={{
        minHeight: 44,
        color: '#009AC7',
        fontSize: 14,
        fontWeight: 700
      }} data-id="element-548">
          Transfer again
        </button>
      </div>
      <div className="flex justify-center pb-2 bg-white" data-id="element-549">
        <div className="w-[120px] h-[5px] rounded-full bg-neutral-900/80" aria-hidden="true" data-id="element-550" />
      </div>
    </div>;
}
function Party({
  label,
  name,
  sub,
  tone
}: {
  label: string;
  name: string;
  sub?: string;
  tone: 'neutral' | 'brand';
}) {
  const isBrand = tone === 'brand';
  return <div className="flex items-center gap-3" data-id="element-551">
      <span className="shrink-0 flex items-center justify-center rounded-xl" style={{
      width: 40,
      height: 40,
      backgroundColor: isBrand ? '#009AC7' : '#F1F5F9',
      color: isBrand ? '#FFFFFF' : '#475569',
      fontSize: 13,
      fontWeight: 800,
      letterSpacing: '0.02em'
    }} data-id="element-552">
        {initials(name)}
      </span>
      <div className="flex-1 min-w-0" data-id="element-553">
        <div style={{
        fontSize: 10.5,
        fontWeight: 700,
        color: '#64748B',
        letterSpacing: '0.08em',
        textTransform: 'uppercase'
      }} data-id="element-554">
          {label}
        </div>
        <div className="mt-0.5 truncate" style={{
        fontSize: 14,
        fontWeight: 700,
        color: '#0F172A'
      }} data-id="element-555">
          {name}
        </div>
        {sub && <div className="mt-0.5 truncate" style={{
        fontSize: 11.5,
        fontWeight: 500,
        color: '#64748B'
      }} data-id="element-556">
            {sub}
          </div>}
      </div>
    </div>;
}
function DetailRow({
  label,
  value,
  emphasis,
  tone,
  last
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  tone?: 'free';
  last?: boolean;
}) {
  return <div className="flex items-center justify-between py-2" style={{
    borderBottom: last ? 'none' : '1px dashed #E5EAF0'
  }} data-id="element-557">
      <span style={{
      fontSize: 12.5,
      color: '#64748B',
      fontWeight: 500
    }} data-id="element-558">
        {label}
      </span>
      <span className="text-right truncate ml-3" style={{
      fontSize: emphasis ? 14.5 : 13,
      color: tone === 'free' ? '#10B981' : emphasis ? '#0F172A' : '#0F172A',
      fontWeight: emphasis ? 800 : 700
    }} data-id="element-559">
        {value}
      </span>
    </div>;
}
function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase() ?? '').join('') || '—';
}
function parseAmount(s: string) {
  const n = Number((s || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}
function formatAmount(s: string) {
  const n = parseAmount(s);
  return n.toLocaleString('en-LK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
function useNow() {
  // Stable value per render — fine for a wireframe receipt.
  const d = new Date();
  const time = d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
  const date = d.toLocaleDateString([], {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  return `${date} · ${time}`;
}
function useReference() {
  // Deterministic-ish mock reference per mount
  const d = new Date();
  const stamp = `${d.getFullYear().toString().slice(-2)}${(d.getMonth() + 1).toString().padStart(2, '0')}${d.getDate().toString().padStart(2, '0')}`;
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `TX${stamp}${rand}`;
}